import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  TextField,
  Stack,
  Alert,
  InputAdornment,
  Box,
  Typography,
  Card,
  CardContent,
  IconButton
} from '@mui/material';
import { 
  Lock,
  Visibility,
  VisibilityOff,
  CheckCircle,
  LockReset
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { EasyCheckButton } from '../../common/Controls';
import { easyCheckAuthService } from '../../../services/auth/authService';
import { themeUtils } from '../../../theme';
import { useNavigate } from 'react-router-dom';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 25px ${themeUtils.colors.blue['blue-600']}20`,
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 25px ${themeUtils.colors.blue['blue-600']}30`,
    },
    '& fieldset': {
      borderColor: themeUtils.colors.blue['blue-100'],
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: themeUtils.colors.blue['blue-300'],
    },
    '&.Mui-focused fieldset': {
      borderColor: themeUtils.colors.blue['blue-600'],
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    backgroundColor: 'transparent',
    '&.Mui-focused': {
      color: themeUtils.colors.blue['blue-600'],
    },
    '&.MuiInputLabel-shrink': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      padding: '0 8px',
      marginLeft: '-4px',
    },
  },
}));

const ResetPasswordForm = ({ username, resetToken, onSuccess }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm({
    defaultValues: {
      newPassword: '',
      confirmPassword: ''
    }
  });

  const newPassword = watch('newPassword');

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError('');
      
      const result = await easyCheckAuthService.resetPassword({
        username,
        resetToken,
        newPassword: data.newPassword
      });
      
      if (result.success) {
        setSuccess(true);
        if (onSuccess) onSuccess();
      } else {
        setError(result.message || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  if (success) {
    return (
      <Card sx={{ borderRadius: 3, maxWidth: 400, mx: 'auto' }}>
        <CardContent sx={{ textAlign: 'center', p: 4 }}>
          <CheckCircle sx={{ 
            fontSize: 64, 
            color: 'success.main', 
            mb: 2 
          }} />
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Password Reset Successful!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your password has been successfully reset. You can now login with your new password.
          </Typography>
          <EasyCheckButton
            buttonType="auth"
            variant="contained"
            fullWidth
            onClick={handleBackToLogin}
            sx={{
              background: themeUtils.gradients.primary,
              '&:hover': {
                background: themeUtils.gradients.primaryDark,
              },
            }}
          >
            Back to Login
          </EasyCheckButton>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 3, maxWidth: 400, mx: 'auto' }}>
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <LockReset sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Reset Your Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Enter your new password below
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3}>
            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <Controller
              name="newPassword"
              control={control}
              rules={{ 
                required: 'New password is required',
                minLength: {
                  value: 6,
                  message: 'Password must be at least 6 characters long'
                }
              }}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  label="New Password"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  error={!!errors.newPassword}
                  helperText={errors.newPassword?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: themeUtils.colors.blue['blue-600'], opacity: 0.7 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ 
                            color: themeUtils.colors.blue['blue-600'],
                            opacity: 0.7,
                            '&:hover': {
                              backgroundColor: `${themeUtils.colors.blue['blue-50']}`,
                              opacity: 1,
                            }
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Controller
              name="confirmPassword"
              control={control}
              rules={{ 
                required: 'Please confirm your new password',
                validate: value => value === newPassword || 'Passwords do not match'
              }}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  label="Confirm New Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  fullWidth
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: themeUtils.colors.blue['blue-600'], opacity: 0.7 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          sx={{ 
                            color: themeUtils.colors.blue['blue-600'],
                            opacity: 0.7,
                            '&:hover': {
                              backgroundColor: `${themeUtils.colors.blue['blue-50']}`,
                              opacity: 1,
                            }
                          }}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <EasyCheckButton
              buttonType="auth"
              type="submit"
              variant="contained"
              fullWidth
              loading={loading}
              size="large"
              startIcon={<LockReset />}
              sx={{
                background: themeUtils.gradients.primary,
                '&:hover': {
                  background: themeUtils.gradients.primaryDark,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 12px 35px ${themeUtils.colors.blue['blue-600']}40`,
                },
                '&:active': {
                  transform: 'translateY(0px)',
                },
                padding: '12px 24px',
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: '6px',
                boxShadow: `0 8px 25px ${themeUtils.colors.blue['blue-600']}30`,
              }}
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </EasyCheckButton>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ResetPasswordForm; 